import React, { useEffect, useState } from 'react';
import { Col } from 'react-bootstrap';
import { number, object, string } from 'yup';

import { AdminInfoForm, OrganizationInfoForm, ResourceContent, ResourcePublicLayout } from '../components';
import { API_URL } from '../api';
import { AnyType, Breadcrumb } from '../interfaces';
import {
  addressValidation,
  DBFieldSchema,
  ROUTE_PATHS,
  NOT_REQUIRED,
  validations,
  getConfigurationDataKey
} from '../utils';
import { useSelector } from 'react-redux';
import { tenancyFeatureSelector } from '../redux/selectors';
import { AnyObject } from '@data-driven-forms/react-form-renderer';
import { getNpiValidationMessage } from '../redux/helpers';
import { PrescriberView, RegistrationWelcomeView } from '../views';

export const getOrganizationOnNextSchema = (applicationName: string | undefined) =>
  object({
    administrator_email: string().test('unique-email', 'Email is not unique', async (value: AnyType) => {
      if (!value) return false;

      const queryParams = `emailAddress=${encodeURIComponent(value)}`;
      const url = `${API_URL}/api/User/IsUniqueEmail`;
      const response = await fetch(
        `${url}?${queryParams}&applicationName=${encodeURIComponent(applicationName as string)}`
      );
      const json = await response.json();

      return !!json.isUniqueEmail;
    }),
    administrator_username: string().test('unique-username', 'Username is not unique', async (value: AnyType) => {
      if (!value) return false;

      const queryParams = `userName=${encodeURIComponent(value)}`;
      const url = `${API_URL}/api/User/IsUniqueUserName`;
      const response = await fetch(
        `${url}?${queryParams}&applicationName=${encodeURIComponent(applicationName as string)}`
      );
      const json = await response.json();

      return !!json.isUniqueUserName;
    })
  });

export const organizationSchema = ({ programId }: { programId: number | undefined }) => {
  const prescriberFormSchema: AnyType = useSelector<AnyType>(
    tenancyFeatureSelector(getConfigurationDataKey('form.prescriber-info'))
  );

  return object({
    // OrganizationInfo
    organization_npi: validations.npiValidation().nullable().required(),
    organization_name: validations.orgNameValidation(),
    address_1: validations.addressValidation(),
    address_2: addressValidation(false),
    city: validations.cityValidation(),
    state: validations.stateValidation(),
    zip: validations.zipValidation(),
    tax_id: validations.taxIdValidation(NOT_REQUIRED),
    organization_phone: validations.phoneValidation(),
    organization_fax: validations.phoneValidation(),
    // AdminInfo
    administrator_is_prescriber: string().default('no').required(),
    administrator_prescriber_npi: validations.conditionalValidation(
      'administrator_is_prescriber',
      validations
        .npiValidation()
        .nullable()
        .required()
        .test('administrator_is_prescriber', getNpiValidationMessage(), async function (value) {
          const { organization_npi, administrator_prescriber_npi } = this.parent ?? {};
          if (
            (administrator_prescriber_npi === null || administrator_prescriber_npi === undefined) &&
            (organization_npi === null || organization_npi === undefined)
          )
            return true;

          const url = `${API_URL}/api/register/IsNpiExists`;
          const queryParams = `orgNpi=${organization_npi}&prescriberNpi=${value}&programId=${programId}`;
          if (organization_npi?.toString()?.length == 10 && administrator_prescriber_npi?.toString()?.length == 10) {
            const response = await window.fetch(`${url}?${queryParams}`, {
              method: 'GET'
            });
            const json = await response.json();
            return json?.isSuccess;
          }

          return true;
        }),
      (v: string) => {
        return v === 'yes';
      },
      number()
    ),
    administrator_first_name: validations.nameValidation().label('First Name'),
    administrator_last_name: validations.nameValidation().label('Last Name'),
    administrator_suffix: validations.suffixValidation().label('Suffix'),
    administrator_phone: validations.phoneValidation().label('Phone'),
    administrator_fax: validations.phoneValidation().label('Fax'),
    administrator_email: validations.emailValidation().required(),
    administrator_username: validations.usernameValidation().required(),

    //Prescriber Info
    prescriber_npi: validations
      .npiValidation()
      .nullable()
      .required()
      .test('npi_prescriber_validate', getNpiValidationMessage(), async function () {
        const { prescriber_npi, administrator_is_prescriber, organization_npi } = this.parent ?? {};
        if (prescriber_npi === null || prescriber_npi === undefined) return true;
        if (
          administrator_is_prescriber === 'no' &&
          prescriber_npi?.toString()?.length == 10 &&
          organization_npi?.toString()?.length == 10
        ) {
          const url = `${API_URL}/api/register/IsNpiExists`;
          const queryParams = `orgNpi=${organization_npi}&physicianNpi=${prescriber_npi}&programId=${programId}`;
          const response = await window.fetch(`${url}?${queryParams}`, {
            method: 'GET'
          });
          const json = await response.json();
          return json?.isSuccess;
        }
        return true;
      }),
    prescriber_first_name: validations.nameValidation().label('First Name'),
    prescriber_middle_name: validations.nameValidation(NOT_REQUIRED).label('Middle Name'),
    prescriber_last_name: validations.nameValidation().label('Last Name'),
    prescriber_suffix: validations.suffixValidation(),
    prescriber_tax_id: validations.taxIdValidation(NOT_REQUIRED),
    prescriber_state_license: string().notRequired().label('State License #'),
    prescriber_licensing_state: validations.conditionalValidation(
      'prescriber_state_license',
      string().required().label('License State'),
      (value: string) => !!value
    ),
    prescriber_ptan: validations.ptanValidation(prescriberFormSchema?.fields?.prescriber_ptan?.required ?? false),
    prescriber_dea: validations.deaValidation(prescriberFormSchema?.fields?.prescriber_dea?.required ?? false),
    prescriber_hin: validations.hinValidation(prescriberFormSchema?.fields?.prescriber_hn?.required ?? false),
    prescriber_email: validations.emailValidation(prescriberFormSchema?.fields?.prescriber_email?.required ?? false)
  });
};

// const breadcrumbs: Breadcrumb[] = [
//   {
//     href: ROUTE_PATHS.registrationWelcome,
//     label: '<< BACK TO WELCOME',
//     testId: 'welcome'
//   }
// ];
//Commenting this since we are no longer going to use breadcrumbs for registration page

export const OrganizationView = () => {
  const [organizationFormFields, setOrganizationFormFields]: [DBFieldSchema, AnyType] = useState({});
  const [administratorFormFields, setAdministratorFormFields]: [DBFieldSchema, AnyType] = useState({});
  const [prescriberFormFields, setPrescriberFormFields]: [DBFieldSchema, AnyType] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const getOrganizationFormSchema = async () => {
    const organizationFormSchema: AnyType = useSelector<AnyType>(
      tenancyFeatureSelector(getConfigurationDataKey('form.organization-info'))
    );
    if (organizationFormSchema) {
      const { fields } = await organizationFormSchema;
      setOrganizationFormFields(fields);
    }
  };
  getOrganizationFormSchema();

  const getAdministratorFormSchema = async () => {
    const administratorFormSchema: AnyType = await useSelector<AnyType>(
      tenancyFeatureSelector(getConfigurationDataKey('form.administrator-info'))
    );
    if (administratorFormSchema) {
      const { fields } = await administratorFormSchema;
      setAdministratorFormFields(fields);
    }
  };
  getAdministratorFormSchema();

  const getPrescriberFormSchema = async () => {
    const prescriberFormSchema: AnyType = await useSelector<AnyType>(
      tenancyFeatureSelector(getConfigurationDataKey('form.prescriber-info'))
    );
    if (prescriberFormSchema) {
      const { fields } = await prescriberFormSchema;
      setPrescriberFormFields(fields);
      setIsLoading(false);
    }
  };
  getPrescriberFormSchema();

  useEffect(() => {
    if (!isEmpty(organizationFormFields) && !isEmpty(administratorFormFields) && !isEmpty(prescriberFormFields)) {
      setIsLoading(false);
    }
  }, [organizationFormFields, administratorFormFields, prescriberFormFields]);

  return (
    <ResourcePublicLayout className='view-container' pageTitle='Organization Information'>
      <div className='d-flex flex-column flex-lg-row'>
        <Col lg={12}>
          <div className='px-md-3'>
            <RegistrationWelcomeView />
            <OrganizationInfoForm isLoading={isLoading} formFields={organizationFormFields} />
            <AdminInfoForm isLoading={isLoading} formFields={administratorFormFields} />
            <PrescriberView isLoading={isLoading} formFields={prescriberFormFields} />
          </div>
        </Col>
      </div>
    </ResourcePublicLayout>
  );
};

const isEmpty = (obj: AnyObject) => Object.keys(obj).length === 0;
